import React from "react";
import { Stack, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { LeadThread } from "./leadApi";
import { formatEventDate } from "./leadCellUtils";

interface Props {
  lead: LeadThread;
}

/**
 * Event column: type · guests on the top line, event date with an
 * outline calendar icon, and an optional preferred-hall pin. Emojis
 * are deliberately avoided so the row reads as enterprise CRM, not
 * a chat thread.
 */
export const EventCell = React.memo(function EventCell({ lead }: Props) {
  const eventLabel = lead.event_type
    ? lead.event_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null;
  const guests  = lead.guest_count != null ? `${lead.guest_count.toLocaleString()} guests` : null;
  const meta    = [eventLabel, guests].filter(Boolean).join(" · ");
  const dateStr = formatEventDate(lead.event_date);

  return (
    <Stack spacing={0.5}>
      <Typography sx={{
        fontSize: "0.78rem", fontWeight: 600,
        color: meta ? "#1e293b" : "#94a3b8",
      }}>
        {meta || "—"}
      </Typography>

      <Stack direction="row" alignItems="center" gap={0.6}>
        <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.71rem", color: "#64748b" }}>
          {dateStr}
        </Typography>
      </Stack>

      {lead.preferred_hall && (
        <Stack direction="row" alignItems="center" gap={0.6}>
          <RoomOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.67rem", color: "#64748b" }}>
            {lead.preferred_hall}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
});
